// @ts-check
// Formulário de "Enviar e-mail" (spec 2.0.0, fluxos.md seção 20.7; contrato 09 de fluxos, seção 17): escolher o modelo
// (o seletor da área de e-mail, com "Criar modelo" e "Usar um modelo pronto"), o modelo escolhido com a miniatura, o
// assunto e a finalidade, quem recebe e o prazo na fila. Criar um modelo sai do editor e volta para este passo
// (`?node=<id>&emailTemplateId=<id>`, K20).
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { EmailTemplatePicker } from '../../email/email-template-picker.js';
import { EmailTemplateThumbnail } from '../../email-design/email-thumbnail.js';
import { Button } from '../../../ui/button.js';
import { Icon } from '../../../ui/icon.js';
import { FlowField, FlowNotice, fieldProblemText } from '../flow-ui.js';
import { t } from '../../../i18n/index.js';

/** @typedef {import('../flow-node-panel.js').NodeFormProps} NodeFormProps */

export const FLOW_EMAIL_EXPIRY_OPTIONS = Object.freeze([360, 720, 1440, 2880, 4320]);

/** Caminho de volta do editor para um passo (o "Criar modelo" volta para ele). @param {string} flowId @param {string} nodeId */
export const flowNodeReturnPath = (flowId, nodeId) => `/campaigns/flows/${encodeURIComponent(flowId)}?node=${encodeURIComponent(nodeId)}`;

/** @param {NodeFormProps} props */
export function EmailNodeForm({ node, flowId, problems, lookupData, onConfig }) {
  const config = node.config;
  const template = (lookupData.emailTemplates ?? []).find((candidate) => candidate.id === config.templateId) ?? null;
  const [isPicking, setIsPicking] = useState(!config.templateId);
  const templateError = fieldProblemText(problems, 'templateId');
  const templateProblem = problems.find((problem) => problem.code.startsWith('EMAIL_TEMPLATE_'));
  const returnPath = flowNodeReturnPath(flowId, node.id);

  return html`<div class="flow-node-form" data-testid="flow-email-form">
    <${FlowField} label=${t('flows.forms.email.template')} error=${templateError}>
      ${config.templateId && !isPicking
        ? html`<div class="flow-email-selected" data-problem=${templateProblem ? 'true' : undefined}>
            <div class="flow-email-thumbnail">
              ${template ? html`<${EmailTemplateThumbnail} templateId=${template.id} updatedAt=${template.updatedAt} title=${template.name} />` : html`<${Icon} name="IconMail" size=${24} />`}
            </div>
            <div class="flow-email-texts">
              <span class="flow-email-name">${template?.name ?? t('flows.summary.templateMissing')}</span>
              ${template?.subject ? html`<span class="flow-email-subject">${t('flows.forms.email.subject', { subject: template.subject })}</span>` : null}
              ${template ? html`<span class="flow-chip" data-tone=${template.purpose === 'SERVICE' ? 'blue' : 'purple'}>${t(`flows.forms.email.purposes.${template.purpose}`)}</span>` : null}
              <span class="flow-email-actions">
                <a class="flow-link-button" href=${`/campaigns/templates/email/${encodeURIComponent(config.templateId)}`} target="_blank" rel="noopener">
                  <${Icon} name="IconExternalLink" size=${14} />${t('flows.forms.email.open')}
                </a>
                <button type="button" class="flow-link-button" onClick=${() => setIsPicking(true)}><${Icon} name="IconSwitchHorizontal" size=${14} />${t('flows.forms.email.change')}</button>
              </span>
            </div>
          </div>`
        : html`<div class="flow-email-picker">
            <${EmailTemplatePicker} value=${config.templateId} returnPath=${returnPath}
              onChange=${(/** @type {{ templateId: string }} */ picked) => {
                onConfig({ templateId: picked.templateId });
                setIsPicking(false);
                lookupData.reload();
              }} />
            ${config.templateId ? html`<${Button} title=${t('common.cancel')} variant="tertiary" size="small" onClick=${() => setIsPicking(false)} />` : null}
          </div>`}
    <//>
    <${FlowField} label=${t('flows.forms.recipients.label')} htmlFor=${`flow-email-recipient-${node.id}`}>
      <select id=${`flow-email-recipient-${node.id}`} class="text-input" value=${config.recipientMode}
        onChange=${(/** @type {Event} */ event) => onConfig({ recipientMode: /** @type {HTMLSelectElement} */ (event.currentTarget).value })}>
        <option value="POINT_OF_CONTACT">${t('flows.forms.recipients.POINT_OF_CONTACT')}</option>
        <option value="ALL_GUARDIANS">${t('flows.forms.recipients.ALL_GUARDIANS')}</option>
      </select>
    <//>
    <${FlowField} label=${t('flows.forms.email.expiry')} htmlFor=${`flow-email-expiry-${node.id}`} hint=${t('flows.forms.email.expiryHint')}
      error=${fieldProblemText(problems, 'expiresInMinutes')}>
      <select id=${`flow-email-expiry-${node.id}`} class="text-input" value=${String(config.expiresInMinutes)}
        onChange=${(/** @type {Event} */ event) => onConfig({ expiresInMinutes: Number(/** @type {HTMLSelectElement} */ (event.currentTarget).value) })}>
        ${FLOW_EMAIL_EXPIRY_OPTIONS.map((minutes) => html`<option key=${minutes} value=${String(minutes)}>${t('flows.forms.email.hours', { count: minutes / 60 })}</option>`)}
      </select>
    <//>
    <${FlowNotice} icon="IconShieldCheck">${t('flows.forms.email.consentNotice')}</${FlowNotice}>
  </div>`;
}
